import { useState } from "react";
import {
  weeklyRanking,
  monthlyRanking,
  allTimeRanking,
  comics,
  type RankingItem,
} from "@/mocks/comics";
import SectionHeading from "./SectionHeading";

const tabs = [
  { key: "week", label: "本周榜", data: weeklyRanking },
  { key: "month", label: "本月榜", data: monthlyRanking },
  { key: "all", label: "总榜", data: allTimeRanking },
] as const;

const trendIcon: Record<RankingItem["trend"], string> = {
  up: "ri-arrow-up-line",
  down: "ri-arrow-down-line",
  flat: "ri-subtract-line",
};

function RankRow({ item, index }: { item: RankingItem; index: number }) {
  const comic = comics.find((c) => c.id === item.comicId);
  if (!comic) return null;
  const topThree = index < 3;

  return (
    <article
      className={`flex items-center gap-4 rounded-2xl border p-3 transition-colors md:gap-5 md:p-4 ${
        topThree
          ? "border-primary-200 bg-primary-50/70"
          : "border-background-200 bg-background-50 hover:border-background-300"
      }`}
    >
      <span
        className={`w-8 shrink-0 text-center font-heading text-xl ${
          index === 0
            ? "text-primary-600"
            : index === 1
              ? "text-accent-600"
              : index === 2
                ? "text-secondary-600"
                : "text-foreground-400"
        }`}
      >
        {index + 1}
      </span>

      <div className="relative h-20 w-14 shrink-0 overflow-hidden rounded-lg bg-background-100 md:h-24 md:w-16">
        <img
          src={comic.cover}
          alt={`${comic.title}封面缩略图`}
          className="h-full w-full object-cover"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-heading text-base text-foreground-950 md:text-lg">
            {comic.title}
          </h3>
          {topThree && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-bold text-white">
              热门
            </span>
          )}
        </div>
        <p className="mt-1 hidden text-xs text-foreground-500 sm:block">
          {comic.author} · {comic.chapter}
        </p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {comic.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-600"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="flex items-center gap-1 font-heading text-lg text-primary-600">
          <i className="ri-star-fill text-xs text-accent-500" aria-hidden="true"></i>
          {item.score.toFixed(1)}
        </span>
        <span
          className={`text-xs ${
            item.trend === "up"
              ? "text-secondary-600"
              : item.trend === "down"
                ? "text-foreground-400"
                : "text-foreground-400"
          }`}
        >
          <i className={trendIcon[item.trend]} aria-hidden="true"></i>
          {item.trend === "up" ? "上升" : item.trend === "down" ? "下降" : "持平"}
        </span>
      </div>
    </article>
  );
}

export default function Ranking() {
  const [active, setActive] = useState<(typeof tabs)[number]["key"]>("week");
  const current = tabs.find((t) => t.key === active) ?? tabs[0];

  return (
    <section id="ranking" className="bg-background-100/70 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="高分排行榜"
          title="香香漫画高分排行"
          subtitle="综合用户评分、收藏与热度实时生成的高分榜单。谁是当之无愧的口碑之王？每周、每月持续刷新，让你不再错过任何一部高分好漫画。"
        />

        <div
          className="mb-8 inline-flex items-center rounded-full border border-background-200 bg-background-50 p-1"
          role="tablist"
          aria-label="排行榜切换"
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active === tab.key}
              onClick={() => setActive(tab.key)}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm transition-colors ${
                active === tab.key
                  ? "bg-primary-500 text-white"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
          {current.data.map((item, index) => (
            <RankRow key={item.comicId} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}